// src/views/ConfiguracionGral/ParametrosGrales/EditarParametros.jsx

import React, { useEffect, useMemo, useState } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";
import { appFetchJson } from "../../../utils/http";
import InputNumberNoScroll from "../../../components/inputs/inputNumberNoScroll";
import BtnGuardar from "../../../components/buttons/BtnGuardar";

/* ===========================================
   Normaliza fecha: "29-ene-2015 0:00:00" → "2015-01-29"
   =========================================== */
function normalizarFecha(valor) {
  if (!valor) return "";

  const parte = valor.split(" ")[0]; // "29-ene-2015"
  const [dia, mesTxt, año] = parte.split("-");

  const meses = {
    ene: "01",
    feb: "02",
    mar: "03",
    abr: "04",
    may: "05",
    jun: "06",
    jul: "07",
    ago: "08",
    sep: "09",
    oct: "10",
    nov: "11",
    dic: "12",
  };

  const mes = meses[mesTxt?.toLowerCase()];
  if (!mes) return "";

  return `${año}-${mes}-${String(dia).padStart(2, "0")}`;
}

function mostrarFecha(valor) {
  if (!valor) return "";

  // valor esperado: "2025-12-17"
  const [y, m, d] = valor.split("-");
  if (!y || !m || !d) return "";

  return `${d}/${m}/${y}`;
}

/* ===========================================
   COMPONENTE PRINCIPAL
   =========================================== */
export default function EditarParametros({
  initial,
  onClose,
  onVolver,
  onSaved,
  onGuardado,
}) {
  const closeFn = onClose || onVolver || (() => {});
  const savedFn = onSaved || onGuardado || (() => {});

  // ESC para cerrar
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeFn();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeFn]);

  const id = useMemo(() => initial?.id ?? null, [initial]);

  // Campos =====================================
  const [nombre, setNombre] = useState(initial?.nombre || "");
  const [valor, setValor] = useState(initial?.valor || "");

  const [fecha, setFecha] = useState(
    initial?.fecha ? normalizarFecha(initial.fecha) : "",
  );
  const [numero, setNumero] = useState(initial?.numero || "");

  // tu mock usa "prioridad", no "pr"
  const [pr, setPr] = useState(initial?.prioridad || "");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // API base -- Comentado
  //const API_BASE =
    //(apiRoutes?.login || "").replace(/\/login.*$/, "") || "/flpa/api";

  const UPDATE_URL = apiRoutes.parametros.update(id);

  // Detectar cambios
  const changed =
    nombre !== initial?.nombre ||
    valor !== initial?.valor ||
    fecha !== normalizarFecha(initial?.fecha || "") ||
    numero !== initial?.numero ||
    pr !== (initial?.prioridad || "");

  // Guardar
  const handleSave = async () => {
    if (!id || !changed) return;

    setError("");
    setSaving(true);

    try {
      const body = {
        nombre,
        valor,
        fecha,
        numero,
        prioridad: pr,
      };

      const updated = await apiFetchJson(UPDATE_URL, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      savedFn(updated);
      closeFn();
    } catch (e) {
      setError(e?.message || "Ocurrió un error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  /* ===========================================
     RENDER
     =========================================== */
  return (
    <div className="modal-backdrop" onClick={closeFn}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2 className="section-title">Editar Parámetro</h2>
        </div>

        {/* Body */}
        <div className="modal-body">
          {error && <div className="mb-3 text-red-600">{error}</div>}

          <div className="form-grid">
            {/* Nombre */}
            <FilaEdit
              label="Nombre"
              input={
                <input
                  className="input"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                />
              }
            />

            {/* Valor */}
            <FilaEdit
              label="Valor"
              input={
                <input
                  className="input"
                  value={valor}
                  onChange={(e) => setValor(e.target.value)}
                />
              }
            />

            {/* Fecha */}
            <FilaEdit
              label="Fecha"
              input={
                <>
                  <input
                    type="date"
                    className="input"
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {fecha ? mostrarFecha(fecha) : ""}
                  </div>
                </>
              }
            />

            {/* Numero */}
            <FilaEdit
              label="Número"
              input={
                <InputNumberNoScroll
                  className="input w-full h-[38px] bg-white border rounded px-3"
                  value={numero}
                  onChange={(e) => setNumero(e.target.value)}
                />
              }
            />

            {/* Prioridad */}
            <FilaEdit
              label="Prioridad"
              input={
                <InputNumberNoScroll
                  className="input w-full h-[38px] bg-white border rounded px-3"
                  value={pr}
                  onChange={(e) => setPr(e.target.value)}
                />
              }
            />

          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div className="btn-row">
            <BtnGuardar
  label="Guardar"
  isValid={changed}
  saving={saving}
  onClick={handleSave}
/>

            <button type="button" className="btn-fil" onClick={closeFn}>
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===========================================
   SUBCOMPONENTE
   =========================================== */
function FilaEdit({ label, input, help }) {
  return (
    <div className="form-row gap-2 items-start">
      <div className="form-label">{label}:</div>
      <div className="flex-1">
        {input}
        {help && <div className="text-xs text-gray-500 mt-1">{help}</div>}
      </div>
    </div>
  );
}
